import { useReducer, useEffect, useCallback, Reducer, Dispatch } from "react";

type Options<S> = {
  storageKey: string;
  syncedKeys?: (keyof S)[];
};

export function useSyncedReducer<
  State extends { isHydrated?: boolean },
  Action
>(
  reducer: Reducer<State, Action>,
  initialState: State,
  { storageKey, syncedKeys }: Options<State>
): [State, Dispatch<Action>] {
  const init = useCallback(
    (defaultState: State): State => {
      try {
        const storedData = localStorage.getItem(storageKey);
        if (!storedData) {
          return {
            ...defaultState,
            isHydrated: true,
          };
        }

        const parsed = JSON.parse(storedData);

        const merged = !syncedKeys
          ? { ...defaultState, ...parsed }
          : {
              ...defaultState,
              ...syncedKeys.reduce((acc, key) => {
                acc[key] = parsed[key];
                return acc;
              }, {} as Partial<State>),
            };

        return {
          ...merged,
          isHydrated: true,
        };
      } catch (err) {
        console.error(
          `[useSyncedReducer] Failed to init from localStorage`,
          err
        );
        return {
          ...defaultState,
          isHydrated: true,
        };
      }
    },
    [storageKey, syncedKeys]
  );

  const [state, dispatch] = useReducer(reducer, initialState, init);

  useEffect(() => {
    try {
      const toPersist = !syncedKeys
        ? state
        : syncedKeys.reduce((acc, key) => {
            acc[key] = state[key];
            return acc;
          }, {} as Partial<State>);

      localStorage.setItem(storageKey, JSON.stringify(toPersist));
    } catch (err) {
      console.error(`[useSyncedReducer] Failed to save to localStorage`, err);
    }
  }, [state, storageKey, syncedKeys]);

  return [state, dispatch];
}

export default useSyncedReducer;

